export type AuthMemberRoleArrayIssueCode = "CONTRACT_MISSING" | "CONTRACT_INVALID";

export type AuthMemberRoleArrayIssue = {
  code: AuthMemberRoleArrayIssueCode;
  message: string;
  path: string;
  value?: unknown;
};

export type AuthMemberRoleArrayErrorFactory = (issue: AuthMemberRoleArrayIssue) => Error;

export type AuthMemberRoleArraySource = {
  application_roles?: unknown;
};

export type ReadRequiredAuthMemberRoleArrayOptions<TRole extends string> = {
  allowedRoles: readonly TRole[];
  createError?: AuthMemberRoleArrayErrorFactory;
  field?: string;
  path?: string;
};

function defaultCreateError(issue: AuthMemberRoleArrayIssue) {
  return new Error(issue.message);
}

function stringifyContractValue(value: unknown) {
  if (typeof value === "string") {
    return value;
  }
  return String(value);
}

function createIssue(
  code: AuthMemberRoleArrayIssueCode,
  path: string,
  suffix?: string,
  value?: unknown,
): AuthMemberRoleArrayIssue {
  const valueSegment = value === undefined ? "" : `:${stringifyContractValue(value)}`;
  return {
    code,
    message: `${code}:${path}${suffix ? `:${suffix}` : ""}${valueSegment}`,
    path,
    ...(value === undefined ? {} : { value }),
  };
}

export function readRequiredAuthMemberRoleArray<TRole extends string>(
  source: AuthMemberRoleArraySource | null | undefined,
  options: ReadRequiredAuthMemberRoleArrayOptions<TRole>,
): TRole[] {
  const {
    allowedRoles,
    createError = defaultCreateError,
    field = "application_roles",
    path = `current_user.${field}`,
  } = options;
  if (source === null || source === undefined) {
    throw createError(createIssue("CONTRACT_MISSING", path));
  }
  const value = source[field as keyof AuthMemberRoleArraySource];
  if (value === null || value === undefined) {
    throw createError(createIssue("CONTRACT_MISSING", path));
  }
  if (!Array.isArray(value)) {
    throw createError(createIssue("CONTRACT_INVALID", path, "expected_array", value));
  }

  const allowedRoleSet = new Set<string>(allowedRoles);
  const seenRoles = new Set<string>();
  return value.map((role, index) => {
    const rolePath = `${path}[${index}]`;
    if (typeof role !== "string") {
      throw createError(createIssue("CONTRACT_INVALID", rolePath, "expected_string", role));
    }
    if (!allowedRoleSet.has(role)) {
      throw createError(createIssue("CONTRACT_INVALID", rolePath, "unknown_role", role));
    }
    if (seenRoles.has(role)) {
      throw createError(createIssue("CONTRACT_INVALID", rolePath, "duplicate_role", role));
    }
    seenRoles.add(role);
    return role as TRole;
  });
}
